import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "./Header";

const HERO_IMG =
  "https://images.pexels.com/photos/27402391/pexels-photo-27402391.jpeg?auto=compress&cs=tinysrgb&w=2000";

const lineWrap = {
  hidden: {},
  show: (i) => ({ transition: { staggerChildren: 0, delayChildren: 0.35 + i * 0.16 } }),
};

const lineInner = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const RouteLines = () => (
  <svg
    className="absolute right-[-10%] top-[12%] w-[60vw] max-w-[820px] opacity-25 pointer-events-none"
    viewBox="0 0 800 600"
    fill="none"
    aria-hidden="true"
  >
    <path
      className="route-dash"
      d="M40 540 C 220 420, 300 460, 420 340 S 640 220, 760 90"
      stroke="#2DD4BF"
      strokeWidth="1.5"
    />
    <path d="M40 560 C 240 470, 360 500, 500 380 S 700 260, 780 160" stroke="white" strokeOpacity="0.25" strokeWidth="1" />
    <path d="M60 500 C 200 380, 320 400, 460 300 S 620 180, 740 60" stroke="white" strokeOpacity="0.15" strokeWidth="1" />
    {[
      [40, 540],
      [420, 340],
      [760, 90],
    ].map(([cx, cy]) => (
      <g key={`${cx}-${cy}`}>
        <circle cx={cx} cy={cy} r="6" fill="#02060D" stroke="#2DD4BF" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="2" fill="#2DD4BF" />
      </g>
    ))}
  </svg>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen overflow-hidden flex flex-col">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Aerial view of a container ship at sea"
          className="h-[115%] w-full object-cover saturate-[0.75]"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#02060D] via-[#02060D]/40 to-[#02060D]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#02060D]/70 via-transparent to-transparent" />
      </motion.div>

      <RouteLines />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col justify-end mx-auto w-full max-w-7xl px-6 lg:px-10 pb-10 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase text-teal-400 font-medium mb-6"
          data-testid="hero-overline"
        >
          Supply Chain · Logistics · In-Country Value — Point Frederick, NSW
        </motion.p>

        <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[1.05] font-medium text-slate-50 max-w-5xl">
          {[
            <span key="a">Defined problems.</span>,
            <span key="b" className="italic text-teal-300">Solved precisely.</span>,
            <span key="c">No permanent headcount.</span>,
          ].map((line, i) => (
            <motion.span key={i} className="block overflow-hidden pb-1" variants={lineWrap} initial="hidden" animate="show" custom={i}>
              <motion.span className="block" variants={lineInner}>
                {line}
              </motion.span>
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-slate-300"
          data-testid="hero-subcopy"
        >
          Coastal Logistics Solutions provides senior, flexible supply chain, logistics and ICV
          support — extra management capacity and mentoring for organisations that need experienced
          hands now, not another permanent hire.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={PHONE_TEL}
            data-testid="hero-call-james-btn"
            className="inline-flex items-center gap-2.5 rounded-full bg-teal-400 px-7 py-3.5 text-sm md:text-base font-semibold text-[#02060D] hover:bg-teal-300 hover:shadow-[0_0_32px_rgba(45,212,191,0.4)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
          >
            <Phone className="h-4 w-4" />
            Call James — {PHONE_DISPLAY}
          </a>
          <a
            href="#capabilities"
            data-testid="hero-explore-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm md:text-base font-medium text-slate-100 hover:border-teal-300 hover:text-teal-300 transition-colors duration-300"
          >
            Explore capabilities
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-white/10 pt-8"
          data-testid="hero-stats"
        >
          {[
            { value: "40+ years", label: "Across Defence, Government, mining & complex programs" },
            { value: "Local & overseas", label: "Based at Point Frederick, NSW — engaged anywhere" },
            { value: "Days to months", label: "Flexible engagements shaped around the problem" },
          ].map((s) => (
            <div key={s.value}>
              <p className="font-display text-xl md:text-2xl text-slate-50">{s.value}</p>
              <p className="mt-1 text-xs md:text-sm text-slate-400 leading-relaxed">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="relative z-10 flex justify-center pb-6">
        <ArrowDown className="h-5 w-5 text-teal-400 animate-scroll-cue" aria-hidden="true" />
      </div>
    </section>
  );
}
